import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ClientCard } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Archive, Search } from "lucide-react";

export default function ArchivioClienti() {
  const navigate = useNavigate();
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const loadArchived = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: { archived: true } });
      setArchived(data || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArchived();
  }, [loadArchived]);

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = [...archived].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "it", { sensitivity: "base" }),
    );
    if (q) {
      list = list.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.company || "").toLowerCase().includes(q) ||
          (c.contact_email || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [archived, search]);

  return (
    <div className="space-y-6" data-testid="archive-clients-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <button
            data-testid="archive-back-button"
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white mb-2"
          >
            <ArrowLeft size={14} /> Torna ai clienti
          </button>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Archivio</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mt-1 flex items-center gap-3">
            <Archive className="text-amber-300" size={28} />
            Archivio clienti{" "}
            <span className="text-zinc-500 font-normal tabular-nums" data-testid="archive-count">
              ({sorted.length})
            </span>
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            I clienti archiviati non compaiono nella sezione principale, ma task e credenziali
            restano salvate. Usa "Ripristina cliente" per riportarlo tra i clienti attivi.
          </p>
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input
            data-testid="archive-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca cliente archiviato..."
            className="h-10 pl-9 pr-3 rounded-md bg-white/5 border border-white/10 text-white text-sm w-56 focus:outline-none focus:border-fuchsia-400/60 placeholder:text-zinc-500"
          />
        </div>
      </div>

      {loading && <div className="text-zinc-500 text-sm">Caricamento…</div>}

      {!loading && sorted.length === 0 && (
        <div className="tp-glass rounded-3xl p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
            <Archive className="text-zinc-400" />
          </div>
          <h3 className="text-xl font-semibold text-white">Nessun cliente archiviato</h3>
          <p className="text-sm text-zinc-400 mt-1">
            Quando archivi un cliente dalla sezione principale, lo trovi qui.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {sorted.map((c) => (
          <ClientCard key={c.id} client={c} archiveMode onChanged={loadArchived} />
        ))}
      </div>
    </div>
  );
}
